import { decode } from 'he';
import { Fragment } from 'react';
import ReactPlayer from 'react-player';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { DefaultLinkSimple } from '@hivebrite/design-system/Link';
import { Play } from '@hivebrite/design-system/Play';
import { onEnterPress } from '@hivebrite/utils';

import { MEDIA_CENTER_COVER_SIZE } from 'hb-react/backoffice/apps/MediaCenter/constants';
import { formatFileExtension } from 'hb-react/shared/components/Thumbnails/FileThumbnail/utils/fileUtils';
import {
  isFileAnAudio,
  isFileAVideo,
  isFileExternalSource,
  isFolder,
} from 'hb-react/shared/utils/files/fileContentTypes';

import ExternalSourceThumbnail from './ExternalSourceThumbnail';
import WrapperContent from './WrapperContent';

interface Content {
  content_type: string;
  cover_picture_file_url: string;
  extension: string;
  file_type: string;
  name: string;
  thumbnail_url: string;
  type: string;
  url: string;
}

interface PreviewImageProps {
  content: Content;
  sharedFrom: string;
}

interface ContentThumbnailProps {
  content: Content;
  onClick?: () => void;
  sharedFrom: string;
  sharedUrl?: string;
}

const PreviewImage = ({ content, sharedFrom }: PreviewImageProps) => {
  // Can be a file OR a folder.
  const {
    url,
    name,
    content_type: contentType,
    file_type: fileType,
    thumbnail_url: thumbnailUrl,
    cover_picture_file_url: coverPictureFileUrl,
  } = content;

  const pictureUrl = coverPictureFileUrl || thumbnailUrl;

  const tag = formatFileExtension(content);
  const decodedName = (name && decode(name)) ?? '';
  const isContentFolder = isFolder(contentType);

  if (isFileExternalSource(fileType)) {
    return (
      <ExternalSourceThumbnail url={url} text={name} tag={tag}>
        <Box font="body-semibold-asphalt" mt={3} px={3} pb={1} fontSize={3}>
          {content.name}
        </Box>
        <Box font="body-regular-asphalt" px={3} mb={3} fontSize={3}>
          {sharedFrom}
        </Box>
      </ExternalSourceThumbnail>
    );
  }

  let preview = (
    <NoImageCover>
      <Dotdotdot clamp={3}>{decodedName}</Dotdotdot>
    </NoImageCover>
  );

  if (pictureUrl) {
    preview = <StyledImg $source={pictureUrl} />;
  }

  if (isFileAVideo(fileType) || isFileAnAudio(fileType)) {
    preview = (
      <>
        <WrapperPlay>
          <Play ariaLabel={__('Start the video preview')} onClick={() => {}} />
        </WrapperPlay>
        <VideoDimmer />
        <ReactPlayer className="player" url={url} width="100%" height="auto" />
      </>
    );
  }

  return (
    <WrapperContent
      tag={tag}
      isFolder={isContentFolder}
      alignItems="center"
      justifyContent="center"
      font="h2-regular"
      preview={preview}
    >
      <Box
        alignSelf="flex-start"
        display="block"
        font="body-semibold-asphalt"
        fontSize={3}
        mt={3}
        overflow="hidden"
        pb={1}
        px={3}
        style={{ textOverflow: 'ellipsis' }}
        width="100%"
      >
        {content.name}
      </Box>

      <Box
        font="body-regular-asphalt"
        alignSelf="flex-start"
        px={3}
        mb={3}
        fontSize={3}
      >
        {sharedFrom}
      </Box>
    </WrapperContent>
  );
};

export const ContentThumbnail = ({
  content,
  sharedFrom,
  sharedUrl,
  onClick,
}: ContentThumbnailProps) => {
  const handleOnClick = () => {
    if (content.file_type === 'external') {
      return;
    }

    onClick?.();
  };

  const Wrapper =
    content.file_type === 'external' && sharedUrl
      ? ({ children }) => (
          <DefaultLinkSimple target="_blank" href={sharedUrl}>
            {children}
          </DefaultLinkSimple>
        )
      : Fragment;

  return (
    <Wrapper>
      <ContentThumbnailWrapper
        flexDirection="column"
        tabIndex={0}
        onClick={handleOnClick}
        onKeyPress={onEnterPress(handleOnClick)}
        $sharedUrl={sharedUrl}
      >
        <PreviewImage content={content} sharedFrom={sharedFrom} />
      </ContentThumbnailWrapper>
    </Wrapper>
  );
};

const ContentThumbnailWrapper = styled(Box).attrs(() => ({
  mx: -6,
  px: 6,
  pt: 3,
  pb: 4,
  bg: 'smoke',
}))<{ $sharedUrl?: string }>`
  ${({ $sharedUrl }) => css`
    cursor: ${$sharedUrl ? 'pointer' : 'default'};
  `};
`;

const WrapperPlay = styled(Box).attrs(() => ({
  position: 'absolute',
  top: '50%',
  right: '50%',
}))`
  z-index: 2;
  transform: translate(50%, -50%);
`;

const VideoDimmer = styled(Box)`
  background-color: black;
  width: 100%;
  aspect-ratio: ${() => MEDIA_CENTER_COVER_SIZE.aspectRatio};
  left: 0;
  opacity: 0.3;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px ${theme.themeVariables.globalBorderRadius}px 0 0`};
`;

const StyledImg = styled(Box)<{ $source: string }>`
  width: 100%;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px ${theme.themeVariables.globalBorderRadius}px 0 0`};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  aspect-ratio: ${() => MEDIA_CENTER_COVER_SIZE.aspectRatio};
  ${({ $source }) =>
    $source &&
    css`
      background-image: url('${$source}');
    `};
`;

const NoImageCover = styled(Box).attrs(() => ({
  font: 'h2-regular',
  fontSize: 5,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  width: '100%',
  p: 5,
}))`
  aspect-ratio: ${() => MEDIA_CENTER_COVER_SIZE.aspectRatio};
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.colors.cloud};
`;
